import { Card, ErrorState, LoadingState } from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { Meter } from "../../../components/Meter";
import type { DashboardStats } from "../../../lib/queries/dashboard";
import styles from "./DashboardPage.module.css";

// „Sezóna v číslech" — what replaced the four raw counts (Federace: 1,
// Uživatelé: 12) that answered nothing.
//
// Every figure is season-scoped and every ratio says what it is a share of;
// a bare percentage is a riddle. The numbers come from the service rather
// than being folded client-side so that „má soupisku" is defined in exactly
// one place — the same predicate the team_unpaid alert uses, which is what
// keeps `with_roster − paid` and that alert's count from contradicting each
// other on this very screen.

/** Whole-percent share, 0 when there is nothing to be a share of. */
function share(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

export interface SeasonNumbersCardProps {
  stats: DashboardStats | undefined;
  isLoading?: boolean;
  error?: unknown;
}

export function SeasonNumbersCard({ stats, isLoading, error }: SeasonNumbersCardProps) {
  return (
    <Card>
      <Card.Header
        title="Sezóna v číslech"
        subtitle={stats?.season?.code ?? "—"}
        subtitlePlacement="right"
      />
      <Card.Content>
        {isLoading ? (
          <LoadingState label="Načítám čísla sezóny…" />
        ) : error ? (
          <ErrorState error={error} title="Nepodařilo se načíst čísla sezóny." />
        ) : !stats ? null : (
          <div className={styles.figures}>
            <div className={styles.fact}>
              <div className={styles.factLabel}>Registrovaní členové</div>
              <div className={styles.factValue}>{stats.members.registered}</div>
              <div className={styles.figureCaption}>
                {stats.members.guests > 0
                  ? `+ ${stats.members.guests} ${pluralizeCzech(stats.members.guests, ["nečlen", "nečlenové", "nečlenů"])}`
                  : `napříč ${stats.teams.active} ${pluralizeCzech(stats.teams.active, ["aktivním týmem", "aktivními týmy", "aktivními týmy"])}`}
              </div>
            </div>

            <div className={styles.fact}>
              <div className={styles.factLabel}>Týmy se soupiskou</div>
              <div className={styles.factValue}>
                {stats.teams.with_roster} / {stats.teams.active}
              </div>
              <Meter
                value={stats.teams.with_roster}
                max={stats.teams.active}
                label="Týmy se soupiskou"
              />
              <div className={styles.figureCaption}>
                {`${share(stats.teams.with_roster, stats.teams.active)} % aktivních týmů`}
              </div>
            </div>

            <div className={styles.fact}>
              <div className={styles.factLabel}>Zaplaceno</div>
              <div className={styles.factValue}>
                {stats.teams.paid} / {stats.teams.with_roster}
              </div>
              <Meter
                value={stats.teams.paid}
                max={stats.teams.with_roster}
                // Before the uzávěrka nobody owes the fee yet (the team_unpaid
                // alert is silent too), so the ratio is progress, not a debt.
                variant={
                  stats.teams.paid === stats.teams.with_roster
                    ? "ok"
                    : stats.season?.fee_due
                      ? "warn"
                      : "primary"
                }
                label="Zaplacené členské příspěvky"
              />
              <div className={styles.figureCaption}>
                {stats.season?.fee_due
                  ? `${share(stats.teams.paid, stats.teams.with_roster)} % týmů se soupiskou`
                  : "Splatné po uzávěrce registrace"}
              </div>
            </div>

            <div className={styles.fact}>
              <div className={styles.factLabel}>Má fotku</div>
              <div className={styles.factValue}>
                {stats.members.with_photo} / {stats.members.registered}
              </div>
              <Meter
                value={stats.members.with_photo}
                max={stats.members.registered}
                label="Členové s fotkou"
              />
              <div className={styles.figureCaption}>
                {`${share(stats.members.with_photo, stats.members.registered)} % registrovaných členů`}
              </div>
            </div>

            <div className={styles.fact}>
              <div className={styles.factLabel}>Podané přihlášky</div>
              <div className={styles.factValue}>{stats.entries.submitted}</div>
              <div className={styles.figureCaption}>
                {`napříč ${stats.competitions.total} ${pluralizeCzech(stats.competitions.total, ["soutěží", "soutěžemi", "soutěžemi"])}`}
                {stats.competitions.entries_open > 0
                  ? ` · ${stats.competitions.entries_open} otevřeno`
                  : ""}
              </div>
            </div>
          </div>
        )}
      </Card.Content>
    </Card>
  );
}
